import { windowLabel } from '../lib/dates.js'
import { reasonLabel, requestLabel } from '../../../shared/timeoff.js'

/**
 * How an absence is drawn, in the one place it is drawn from.
 *
 * Three grids and a wall used to each carry their own copy of "Off · note" and their own
 * hatch — the wall's in red, the board's in grey — and they drifted, which is how a person
 * ended up looking struck out on one screen and merely away on another. Both markers here
 * ride on one shared stripe (`.off-hatch`, styles.css), and the wall restyles them by size
 * and ink only; the shape is not its to change.
 *
 * Whole day: the badge, on a cell that has been given the stripe and spans the row.
 * Part of a day: a tile that reads like a job — because it sits where jobs sit, in the first
 * column above whatever is booked — with the same stripe saying it is not one. The rest of
 * the row is still the person's to be booked into.
 *
 * `onEdit` is what makes either one a target. Absent, they are plain text: the wall has
 * nobody to click them and a reader without write access has nothing to open. Present, the
 * marker is the handle on the absence it draws — which is where somebody looking at "Off"
 * and wondering what it covers already expects to click.
 */

/**
 * The scheduler-only facts (migration 025), for the tooltip and the amber stripe — and only
 * ever on a marker that is a handle. That is not a guess at who is looking: the crew's payload
 * has no such fields at all, and the markers drawn without `onEdit` are exactly the ones the
 * wall and the printout draw, which is where a scheduler's own board goes out to the crew.
 * The words never reach the marker's face, which a printout would show.
 */
function privateLines(t) {
  return [reasonLabel(t?.reason), requestLabel(t?.requestStatus), t?.internalNote].filter(Boolean)
}
const awaiting = (t) => t?.requestStatus === 'awaiting'

/** Stops the cell underneath from also acting: it carries the board's own click and drop
 *  handlers, so without this the cell's job editor opens behind the time-off dialog. */
function only(fn, t) {
  return (e) => {
    e.stopPropagation()
    fn(t)
  }
}

export function OffBadge({ t, onEdit = null }) {
  const label = `Off${t?.note ? ` · ${t.note}` : ''}`
  if (!onEdit) return <span className="off-badge">{label}</span>
  return (
    <button
      type="button"
      className={`off-badge ${awaiting(t) ? 'awaiting' : ''}`}
      title={[label, ...privateLines(t), 'Click to change'].join('\n')}
      onClick={only(onEdit, t)}
    >
      {label}
    </button>
  )
}

export function OffPartialTile({ t, date, onEdit = null }) {
  // On the day it starts this reads 'from 9am', on the day it ends 'until 1pm', and on a
  // single day the pair. The middle of a period never draws this tile at all — those days
  // are wholly off and get the badge.
  const hours = windowLabel(t, date)
  // One line, the same shape as the whole-day badge: "Off · note", with the hours in it.
  // The note used to hang below as a chip, which made a part-day absence twice the height
  // of a job in the cell beside it and read as its own little card.
  const label = `Off ${hours}${t?.note ? ` · ${t.note}` : ''}`
  if (!onEdit) {
    return (
      <div className="off-partial-tile off-hatch" title={label}>
        <span className="label">{label}</span>
      </div>
    )
  }
  return (
    <button
      type="button"
      className={`off-partial-tile off-hatch ${awaiting(t) ? 'awaiting' : ''}`}
      title={[label, ...privateLines(t), 'Click to change'].join('\n')}
      onClick={only(onEdit, t)}
    >
      <span className="label">{label}</span>
    </button>
  )
}
